import React, { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Star } from 'lucide-react';

const TestimonialCard = memo(({ testimonial, isLight, isCopy }) => (
  <div
    data-testimonial-card=""
    aria-hidden={isCopy || undefined}
    className={`p-5 rounded-xl mb-4 transition-all duration-300 hover:scale-[1.02] select-none ${
      isLight
        ? 'bg-white border border-gray-100 hover:border-[#d4883a]/30 shadow-sm hover:shadow-md'
        : 'bg-white/5 border border-white/10 hover:border-[#d4883a]/50 hover:bg-white/10'
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex gap-0.5">
        {[...Array(testimonial.rating || 5)].map((_, i) => (
          <Star key={i} className="w-3.5 h-3.5 fill-[#d4883a] text-[#d4883a]" />
        ))}
      </div>
      {testimonial.result && (
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
          isLight ? 'bg-[#d4883a]/10 text-[#d4883a]' : 'bg-[#d4883a] text-white'
        }`}>
          {testimonial.result}
        </span>
      )}
    </div>
    <p className={`text-sm leading-relaxed mb-4 ${isLight ? 'text-gray-600' : 'text-white/80'}`}>
      "{testimonial.text}"
    </p>
    <div className="flex items-center gap-3">
      <div className={`w-9 h-9 min-w-[36px] rounded-full flex items-center justify-center flex-shrink-0 ${
        isLight ? 'bg-gradient-to-br from-[#0b114e] to-[#1a2a5e]' : 'bg-gradient-to-br from-[#d4883a] to-[#e5a55a]'
      }`}>
        <span className="text-white font-semibold text-sm">{testimonial.name?.charAt(0)}</span>
      </div>
      <div className="min-w-0">
        <p className={`font-semibold text-sm truncate ${isLight ? 'text-[#0b114e]' : 'text-white'}`}>
          {testimonial.name}
        </p>
        <p className={`text-xs truncate ${isLight ? 'text-gray-500' : 'text-white/60'}`}>
          {testimonial.role}
        </p>
      </div>
    </div>
  </div>
));
TestimonialCard.displayName = 'TestimonialCard';

const TestimonialWall = memo(({ testimonials, variant = 'light' }) => {
  const column1Ref = useRef(null);
  const column2Ref = useRef(null);
  const column3Ref = useRef(null);
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const positionsRef = useRef([0, 0, 0]);
  const hoveredRef = useRef(false);
  const syncAnimationRef = useRef(null);
  const [copyCounts, setCopyCounts] = useState([2, 2, 2]);
  const copyCountsRef = useRef(copyCounts);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Pausing does not rerender or recreate the cards.
  const handleMouseEnter = useCallback(() => {
    hoveredRef.current = true;
    syncAnimationRef.current?.();
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoveredRef.current = false;
    syncAnimationRef.current?.();
  }, []);

  // Limit testimonials for performance (max 18 total, 6 per column)
  const limitedTestimonials = useMemo(() => {
    return testimonials.slice(0, 18);
  }, [testimonials]);

  // Split testimonials into 3 columns
  const columns = useMemo(() => {
    const cols = [[], [], []];
    limitedTestimonials.forEach((t, i) => {
      cols[i % 3].push(t);
    });
    return cols;
  }, [limitedTestimonials]);

  // A short set needs more than two copies to fill the viewport through the entire loop.
  const column1Items = useMemo(() => Array.from({ length: copyCounts[0] }, () => columns[0]).flat(), [columns, copyCounts]);
  const column2Items = useMemo(() => Array.from({ length: copyCounts[1] }, () => columns[1]).flat(), [columns, copyCounts]);
  const column3Items = useMemo(() => Array.from({ length: copyCounts[2] }, () => columns[2]).flat(), [columns, copyCounts]);

  // For mobile: combine all testimonials into one column
  const mobileItems = useMemo(() => {
    const items = limitedTestimonials.slice(0, 6);
    return Array.from({ length: copyCounts[0] }, () => items).flat();
  }, [limitedTestimonials, copyCounts]);

  useEffect(() => {
    if (limitedTestimonials.length === 0) return;

    const activeColumns = isMobile
      ? [column1Ref.current]
      : [column1Ref.current, column2Ref.current, column3Ref.current];
    const baseCounts = isMobile ? [Math.min(limitedTestimonials.length, 6)] : columns.map(items => items.length);
    const speeds = [18, 24, 21]; // Pixels per second, independent of refresh rate.
    const cycleHeights = [0, 0, 0];
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let reducedMotion = motionQuery?.matches || false;
    let visible = typeof IntersectionObserver === 'undefined';
    let animationId = null;
    let previousTime = null;
    let disposed = false;

    const applyTransforms = () => {
      activeColumns.forEach((column, index) => {
        if (!column) return;
        const offset = reducedMotion ? 0 : (index === 1 && !isMobile
          ? positionsRef.current[index] - cycleHeights[index]
          : -positionsRef.current[index]);
        column.style.transform = `translateY(${offset}px)`;
      });
    };

    const canAnimate = () => !disposed && visible && !hoveredRef.current &&
      !reducedMotion && document.visibilityState !== 'hidden' && cycleHeights.some(height => height > 0);

    const stop = () => {
      if (animationId !== null) cancelAnimationFrame(animationId);
      animationId = null;
      previousTime = null;
    };

    const animate = time => {
      animationId = null;
      if (!canAnimate()) return;
      // A delayed frame must not turn into a visible jump or catch up after a pause.
      const elapsed = previousTime === null ? 0 : Math.min(Math.max(time - previousTime, 0), 64) / 1000;
      previousTime = time;
      activeColumns.forEach((column, index) => {
        const height = cycleHeights[index];
        if (column && height > 0) {
          positionsRef.current[index] = (positionsRef.current[index] + speeds[index] * elapsed) % height;
        }
      });
      applyTransforms();
      animationId = requestAnimationFrame(animate);
    };

    const syncAnimation = () => {
      const running = canAnimate();
      activeColumns.forEach(column => {
        if (column) column.style.willChange = running ? 'transform' : 'auto';
      });
      if (running) {
        if (animationId === null) animationId = requestAnimationFrame(animate);
      } else {
        stop();
      }
    };
    syncAnimationRef.current = syncAnimation;

    const measure = () => {
      // Read layout only when content/width changes, never on animation frames.
      const viewportHeight = containerRef.current?.clientHeight || (isMobile ? 500 : 600);
      const nextCopyCounts = [...copyCountsRef.current];
      activeColumns.forEach((column, index) => {
        if (!column) return;
        // Development's visual editor may insert display:contents wrappers.
        const cards = column.querySelectorAll('[data-testimonial-card]');
        const firstCopy = cards[0];
        const secondCopy = cards[baseCounts[index]];
        // Rects retain fractional card heights and work through display:contents wrappers.
        const copyOffset = firstCopy && secondCopy
          ? secondCopy.getBoundingClientRect().top - firstCopy.getBoundingClientRect().top ||
            secondCopy.offsetTop - firstCopy.offsetTop
          : 0;
        cycleHeights[index] = copyOffset || column.scrollHeight / copyCountsRef.current[index];
        if (cycleHeights[index] > 0) positionsRef.current[index] %= cycleHeights[index];
        // At the most negative offset, one complete set sits above the viewport.
        // Include the last card's 16px gap so no track ending is exposed at the bottom.
        nextCopyCounts[index] = cycleHeights[index] > 0
          ? Math.max(2, Math.ceil((viewportHeight + 16) / cycleHeights[index]) + 1)
          : 2;
      });
      if (nextCopyCounts.some((count, index) => count !== copyCountsRef.current[index])) {
        copyCountsRef.current = nextCopyCounts;
        setCopyCounts(nextCopyCounts);
      }
      applyTransforms();
      syncAnimation();
    };

    const intersectionObserver = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.target === containerRef.current && entry.isIntersecting);
        syncAnimation();
      })
      : null;
    intersectionObserver?.observe(containerRef.current);

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (containerRef.current) resizeObserver?.observe(containerRef.current);
    activeColumns.forEach(column => { if (column) resizeObserver?.observe(column); });
    if (!resizeObserver) window.addEventListener('resize', measure);

    const handleMotionChange = event => {
      reducedMotion = event.matches;
      applyTransforms();
      syncAnimation();
    };
    if (motionQuery?.addEventListener) motionQuery.addEventListener('change', handleMotionChange);
    else motionQuery?.addListener?.(handleMotionChange);
    document.addEventListener('visibilitychange', syncAnimation);
    measure();

    return () => {
      disposed = true;
      stop();
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', measure);
      if (motionQuery?.removeEventListener) motionQuery.removeEventListener('change', handleMotionChange);
      else motionQuery?.removeListener?.(handleMotionChange);
      document.removeEventListener('visibilitychange', syncAnimation);
      if (syncAnimationRef.current === syncAnimation) syncAnimationRef.current = null;
    };
  }, [limitedTestimonials, columns, isMobile]);

  if (testimonials.length === 0) return null;

  const isLight = variant === 'light';

  return (
    <div 
      ref={containerRef}
      className="relative overflow-hidden z-10"
      style={{ height: isMobile ? '500px' : '600px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="testimonial-wall"
    >
      {/* Gradient overlays top and bottom */}
      <div className={`absolute top-0 left-0 right-0 h-24 z-10 pointer-events-none ${
        isLight 
          ? 'bg-gradient-to-b from-[#f8f9fa] to-transparent' 
          : 'bg-gradient-to-b from-[#0b114e] to-transparent'
      }`} />
      <div className={`absolute bottom-0 left-0 right-0 h-24 z-10 pointer-events-none ${
        isLight 
          ? 'bg-gradient-to-t from-[#f8f9fa] to-transparent' 
          : 'bg-gradient-to-t from-[#0b114e] to-transparent'
      }`} />

      {/* Mobile: Single Column */}
      {isMobile ? (
        <div className="h-full px-4">
          <div className="overflow-hidden relative h-full">
            <div ref={column1Ref} className="will-change-transform px-1">
              {mobileItems.map((testimonial, index) => (
                <TestimonialCard key={`mobile-${testimonial.id}-${index}`} testimonial={testimonial} isLight={isLight} isCopy={index >= Math.min(limitedTestimonials.length, 6)} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Desktop: 3 Column Grid */
        <div className="grid grid-cols-3 gap-4 sm:gap-6 h-full px-4" style={{ gridTemplateColumns: `repeat(${Math.min(3, limitedTestimonials.length)}, minmax(0, 1fr))` }}>
          {/* Column 1 - moves up */}
          <div className="overflow-hidden relative">
            <div ref={column1Ref} className="will-change-transform px-1">
              {column1Items.map((testimonial, index) => (
                <TestimonialCard key={`col1-${testimonial.id}-${index}`} testimonial={testimonial} isLight={isLight} isCopy={index >= columns[0].length} />
              ))}
            </div>
          </div>

          {/* Column 2 - moves down */}
          <div className={`overflow-hidden relative ${columns[1].length ? '' : 'hidden'}`}>
            <div ref={column2Ref} className="will-change-transform px-1">
              {column2Items.map((testimonial, index) => (
                <TestimonialCard key={`col2-${testimonial.id}-${index}`} testimonial={testimonial} isLight={isLight} isCopy={index >= columns[1].length} />
              ))}
            </div>
          </div>

          {/* Column 3 - moves up (different speed) */}
          <div className={`overflow-hidden relative ${columns[2].length ? '' : 'hidden'}`}>
            <div ref={column3Ref} className="will-change-transform px-1">
              {column3Items.map((testimonial, index) => (
                <TestimonialCard key={`col3-${testimonial.id}-${index}`} testimonial={testimonial} isLight={isLight} isCopy={index >= columns[2].length} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
TestimonialWall.displayName = 'TestimonialWall';

export default TestimonialWall;
